import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Alert,
  CircularProgress,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Modal,
  Divider,
  TablePagination,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  IconButton,
  Tooltip,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import * as XLSX from 'xlsx';
import { getFunctions, httpsCallable } from 'firebase/functions';

import { db, auth } from '../firebase';
import {
  collection,
  addDoc,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
} from 'firebase/firestore';
import SignInImg from '../img/SignInImg.png';

const functions = getFunctions(auth.app, 'us-central1');

const createEmptyFormData = () => ({
  studentNumber: '',
  firstName: '',
  lastName: '',
  middleName: '',
  rfid: '',
  contactNumber: '',
  course: '',
  yearLevel: '',
  section: '',
  facultyId: '',
  department: '',
  email: '',
});

const getErrorMessage = (error, fallback = 'Unknown error') =>
  error?.message || error?.details || fallback;

const UserRegistry = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState(createEmptyFormData());
  const [courses, setCourses] = useState([]);
  const [yearLevels, setYearLevels] = useState([]);
  const [sections, setSections] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [students, setStudents] = useState([]);
  const [faculties, setFaculties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [formType, setFormType] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(7);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [nameSort, setNameSort] = useState('az');

  const saveFacultyWithAuth = httpsCallable(functions, 'saveFacultyWithAuth');
  const importFacultiesWithAuth = httpsCallable(functions, 'importFacultiesWithAuth');

  const normalizeFacultySnapshot = (snapshot) =>
    snapshot.docs.map((docItem) => {
      const data = docItem.data();
      const rawUserRole = String(data.userRole || '').trim();

      return {
        id: docItem.id,
        ...data,
        authUid: String(data.authUid || '').trim(),
        userRole: rawUserRole || 'Faculty',
      };
    });

  useEffect(() => {
    if (!auth.currentUser) {
      setError('You must be logged in to access this page.');
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setInitialLoading(true);
        setError('');

        const [settingsDoc, studentSnapshot, facultySnapshot] = await Promise.all([
          getDoc(doc(db, 'Settings', 'global')),
          getDocs(collection(db, 'Students')),
          getDocs(collection(db, 'Faculties')),
        ]);

        if (settingsDoc.exists()) {
          const data = settingsDoc.data();
          setCourses(data.courses || []);
          setYearLevels(data.yearLevels || []);
          setSections(data.sections || []);
          setDepartments(data.departments || []);

          if (!data.courses) {
            setError('No courses found in Settings/global.');
          }
        } else {
          setError('Configuration data not found.');
        }

        setStudents(studentSnapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })));
        setFaculties(normalizeFacultySnapshot(facultySnapshot));
      } catch (err) {
        setError('Failed to fetch data: ' + getErrorMessage(err));
      } finally {
        setInitialLoading(false);
      }
    };

    fetchInitialData();
  }, []);

  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  const refreshCollectionData = async (type) => {
    const collectionName = type === 'student' ? 'Students' : 'Faculties';
    const snapshot = await getDocs(collection(db, collectionName));

    if (type === 'student') {
      setStudents(snapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })));
    } else {
      setFaculties(normalizeFacultySnapshot(snapshot));
    }
  };

  const resetForm = () => {
    setFormData(createEmptyFormData());
    setEditMode(false);
    setEditId(null);
    setFormType(null);
  };

  const openAddModal = (type) => {
    setFormType(type);
    setEditMode(false);
    setEditId(null);
    setFormData(createEmptyFormData());
    setModalOpen(true);
  };

  const formatFullName = (person) => {
    const lastName = (person.lastName || '').trim();
    const firstName = (person.firstName || '').trim();
    const middleName = (person.middleName || '').trim();

    const firstMiddle = [firstName, middleName].filter(Boolean).join(' ');

    if (lastName && firstMiddle) return `${lastName}, ${firstMiddle}`;
    if (lastName) return lastName;
    return firstMiddle;
  };

  const handleNameSortToggle = () => {
    setNameSort((prev) => (prev === 'az' ? 'za' : 'az'));
    setPage(0);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setModalOpen(false);
    resetForm();
    setPage(0);
    setSearchTerm('');
    setNameSort('az');
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (id, type) => {
    const data =
      type === 'student'
        ? students.find((student) => student.id === id)
        : faculties.find((faculty) => faculty.id === id);

    if (data) {
      setFormData({
        studentNumber: data.studentNumber || '',
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        middleName: data.middleName || '',
        rfid: data.rfid || '',
        contactNumber: data.contactNumber || '',
        course: data.course || '',
        yearLevel: data.yearLevel || '',
        section: data.section || '',
        facultyId: data.facultyId || '',
        department: data.department || '',
        email: data.email || '',
      });
      setEditMode(true);
      setEditId(id);
      setFormType(type);
      setModalOpen(true);
    } else {
      setError(`Failed to load ${type} data for editing.`);
    }
  };

  const handleSubmit = async (e, type) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const requiredFields =
      type === 'student'
        ? ['studentNumber', 'firstName', 'lastName', 'rfid', 'course', 'yearLevel', 'section']
        : ['facultyId', 'firstName', 'lastName', 'department', 'rfid', 'email'];

    if (requiredFields.some((field) => !String(formData[field] || '').trim())) {
      setError('Please fill in all required fields.');
      setLoading(false);
      return;
    }

    try {
      if (type === 'student') {
        const studentData = {
          studentNumber: String(formData.studentNumber).trim(),
          firstName: String(formData.firstName).trim(),
          lastName: String(formData.lastName).trim(),
          middleName: String(formData.middleName || '').trim(),
          rfid: String(formData.rfid).trim(),
          contactNumber: String(formData.contactNumber || '').trim(),
          course: String(formData.course).trim(),
          yearLevel: String(formData.yearLevel).trim(),
          section: String(formData.section).trim(),
        };

        if (editMode) {
          const docRef = doc(db, 'Students', editId);
          await updateDoc(docRef, studentData);
          setSuccess('Student updated successfully!');
        } else {
          await addDoc(collection(db, 'Students'), studentData);
          setSuccess('Student added successfully!');
        }
      } else {
        const facultyData = {
          facultyId: String(formData.facultyId).trim(),
          firstName: String(formData.firstName).trim(),
          lastName: String(formData.lastName).trim(),
          middleName: String(formData.middleName || '').trim(),
          contactNumber: String(formData.contactNumber || '').trim(),
          department: String(formData.department).trim(),
          rfid: String(formData.rfid).trim(),
          email: String(formData.email || '').trim().toLowerCase(),
          userRole: 'Faculty',
        };

        const response = await saveFacultyWithAuth({
          facultyData,
          docId: editMode ? editId : '',
          resetExistingPassword: true,
        });

        setSuccess(response.data?.message || `Faculty ${editMode ? 'updated' : 'added'} successfully!`);
      }

      resetForm();
      setModalOpen(false);
      await refreshCollectionData(type);
      setPage(0);
    } catch (err) {
      setError(
        `Failed to ${editMode ? 'update' : 'add'} ${
          type === 'student' ? 'student' : 'faculty'
        }: ${getErrorMessage(err)}`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const collectionName = formType === 'student' ? 'Students' : 'Faculties';
      const docRef = doc(db, collectionName, editId);
      await deleteDoc(docRef);

      setSuccess(`${formType === 'student' ? 'Student' : 'Faculty'} removed successfully!`);

      await refreshCollectionData(formType);
      setPage(0);
      setDeleteOpen(false);
      setModalOpen(false);
      resetForm();
    } catch (err) {
      setError(`Failed to delete ${formType === 'student' ? 'student' : 'faculty'}: ${getErrorMessage(err)}`);
    } finally {
      setLoading(false);
    }
  };

  const normalizeHeaderKey = (value) =>
    String(value || '')
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ');

  const getCellValue = (row, possibleKeys) => {
    const normalizedEntries = Object.entries(row).map(([key, value]) => [normalizeHeaderKey(key), value]);

    for (const rawKey of possibleKeys) {
      const normalizedKey = normalizeHeaderKey(rawKey);
      const found = normalizedEntries.find(([key]) => key === normalizedKey);
      if (found) return found[1];
    }

    return '';
  };

  const mapStudentRow = (row) => ({
    studentNumber: String(
      getCellValue(row, ['studentNumber', 'student number', 'student_no', 'student no', 'id number'])
    ).trim(),
    firstName: String(getCellValue(row, ['firstName', 'first name', 'firstname'])).trim(),
    lastName: String(getCellValue(row, ['lastName', 'last name', 'lastname'])).trim(),
    middleName: String(getCellValue(row, ['middleName', 'middle name', 'middlename'])).trim(),
    rfid: String(getCellValue(row, ['rfid', 'rfid number', 'rfid no'])).trim(),
    contactNumber: String(getCellValue(row, ['contactNumber', 'contact number', 'phone', 'mobile'])).trim(),
    course: String(getCellValue(row, ['course', 'program'])).trim(),
    yearLevel: String(getCellValue(row, ['yearLevel', 'year level', 'year'])).trim(),
    section: String(getCellValue(row, ['section'])).trim(),
  });

  const mapFacultyRow = (row) => ({
    facultyId: String(
      getCellValue(row, ['facultyId', 'faculty number', 'faculty no', 'faculty id', 'employee id'])
    ).trim(),
    firstName: String(getCellValue(row, ['firstName', 'first name', 'firstname'])).trim(),
    lastName: String(getCellValue(row, ['lastName', 'last name', 'lastname'])).trim(),
    middleName: String(getCellValue(row, ['middleName', 'middle name', 'middlename'])).trim(),
    contactNumber: String(getCellValue(row, ['contactNumber', 'contact number', 'phone', 'mobile'])).trim(),
    department: String(getCellValue(row, ['department', 'dept'])).trim(),
    rfid: String(getCellValue(row, ['rfid', 'rfid number', 'rfid no'])).trim(),
    email: String(getCellValue(row, ['email', 'email address', 'e-mail'])).trim().toLowerCase(),
    authUid: '',
    userRole: 'Faculty',
  });

  const validateImportedRows = (rows, type) => {
    const requiredFields =
      type === 'student'
        ? ['studentNumber', 'firstName', 'lastName', 'rfid', 'course', 'yearLevel', 'section']
        : ['facultyId', 'firstName', 'lastName', 'department', 'rfid', 'email'];

    return rows.filter((row) => requiredFields.every((field) => String(row[field] || '').trim()));
  };

  const handleImportButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const currentType = tabValue === 0 ? 'faculty' : 'student';
    const collectionName = currentType === 'faculty' ? 'Faculties' : 'Students';

    setImporting(true);
    setError('');
    setSuccess('');

    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: 'array' });
      const firstSheetName = workbook.SheetNames[0];

      if (!firstSheetName) {
        throw new Error('No worksheet found in the selected file.');
      }

      const worksheet = workbook.Sheets[firstSheetName];
      const rawRows = XLSX.utils.sheet_to_json(worksheet, {
        defval: '',
        raw: false,
      });

      if (!rawRows.length) {
        throw new Error('The selected file is empty.');
      }

      const mappedRows =
        currentType === 'student'
          ? rawRows.map(mapStudentRow)
          : rawRows.map(mapFacultyRow);

      const validRows = validateImportedRows(mappedRows, currentType);

      if (!validRows.length) {
        throw new Error(
          currentType === 'student'
            ? 'No valid student rows found. Check your headers and required columns.'
            : 'No valid faculty rows found. Check your headers and required columns.'
        );
      }

      if (currentType === 'student') {
        await Promise.all(validRows.map((row) => addDoc(collection(db, collectionName), row)));

        await refreshCollectionData(currentType);
        setPage(0);

        const skippedCount = mappedRows.length - validRows.length;
        if (skippedCount > 0) {
          setSuccess(
            `${validRows.length} student record(s) imported successfully. ${skippedCount} invalid row(s) were skipped.`
          );
        } else {
          setSuccess(`${validRows.length} student record(s) imported successfully.`);
        }
      } else {
        const response = await importFacultiesWithAuth({
          rows: validRows,
          resetExistingPassword: true,
        });

        await refreshCollectionData(currentType);
        setPage(0);

        const importedCount = Number(response.data?.imported || 0);
        const skippedServerCount = Number(response.data?.skipped || 0);
        const skippedValidationCount = mappedRows.length - validRows.length;
        const totalSkipped = skippedServerCount + skippedValidationCount;

        if (totalSkipped > 0) {
          setSuccess(
            `${importedCount} faculty record(s) imported successfully. ${totalSkipped} row(s) were skipped.`
          );
        } else {
          setSuccess(`${importedCount} faculty record(s) imported successfully.`);
        }
      }
    } catch (err) {
      setError(`Failed to import ${currentType === 'student' ? 'students' : 'faculties'}: ${getErrorMessage(err)}`);
    } finally {
      setImporting(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const baseInputStyles = {
    fontFamily: 'Poppins, sans-serif',
    '& .MuiOutlinedInput-root': {
      borderRadius: '12px',
      fontSize: '0.9rem',
      height: '40px',
      padding: '0 14px',
      transition: 'all 0.3s ease',
      boxSizing: 'border-box',
      '&:hover': {
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#130f72',
        },
      },
      '&.Mui-focused': {
        backgroundColor: 'transparent',
        boxShadow: 'none',
        '& .MuiOutlinedInput-notchedOutline': {
          borderColor: '#130f72',
        },
      },
    },
    '& .MuiInputLabel-root': {
      fontSize: '0.9rem',
      fontFamily: 'Poppins, sans-serif',
      color: '#130f72 !important',
      transform: 'translate(14px, 10px) scale(1)',
      '&.MuiInputLabel-shrink': {
        transform: 'translate(14px, -6px) scale(0.75)',
        color: '#130f72 !important',
      },
      '&.Mui-focused': {
        color: '#130f72 !important',
      },
    },
    '& .MuiOutlinedInput-input': {
      boxSizing: 'border-box',
      textAlign: 'left',
      fontFamily: 'Poppins, sans-serif',
      '&::placeholder': {
        color: 'grey.400',
        opacity: 1,
      },
    },
    '& .MuiSelect-select': {
      padding: '1px 14px 10px 4px',
      paddingBottom: '10px',
      height: '40px !important',
      lineHeight: '40px',
      boxSizing: 'border-box',
      textAlign: 'left',
      fontFamily: 'Poppins, sans-serif',
    },
    '& .MuiInputBase-input::placeholder': {
      color: 'grey.400',
      opacity: 1,
    },
  };

  const inputFieldStyles = {
    ...baseInputStyles,
    width: '250px',
    maxWidth: '250px',
    '& .MuiOutlinedInput-input': {
      ...baseInputStyles['& .MuiOutlinedInput-input'],
      padding: '10px 14px 10px 4px',
      width: '250px',
    },
    '& .MuiSelect-select': {
      ...baseInputStyles['& .MuiSelect-select'],
      width: '250px',
      maxWidth: '250px',
    },
  };

  const reducedInputFieldStyles = {
    ...baseInputStyles,
    width: '96px',
    maxWidth: '96px',
    '& .MuiOutlinedInput-input': {
      ...baseInputStyles['& .MuiOutlinedInput-input'],
      padding: '10px 14px 10px 4px',
      width: '80px',
    },
    '& .MuiSelect-select': {
      ...baseInputStyles['& .MuiSelect-select'],
      width: '80px',
      maxWidth: '80px',
    },
  };

  const yearLevelInputFieldStyles = {
    ...baseInputStyles,
    width: '130px',
    maxWidth: '130px',
    '& .MuiOutlinedInput-input': {
      ...baseInputStyles['& .MuiOutlinedInput-input'],
      padding: '10px 14px 10px 4px',
      width: '130px',
    },
    '& .MuiSelect-select': {
      ...baseInputStyles['& .MuiSelect-select'],
      width: '130px',
      maxWidth: '130px',
    },
  };

  const searchFieldStyles = {
    width: '320px',
    '& .MuiOutlinedInput-root': {
      height: '42px',
      borderRadius: '12px',
      fontFamily: 'Poppins, sans-serif',
      backgroundColor: '#ffffff',
      transition: 'all 0.3s ease',
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(0, 0, 0, 0.23)',
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#130f72',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#130f72',
      },
    },
    '& .MuiInputBase-input': {
      fontFamily: 'Poppins, sans-serif',
      '&::placeholder': {
        color: 'grey.400',
        opacity: 1,
      },
    },
  };

  const addButtonStyles = {
    py: 1,
    borderRadius: '12px',
    boxShadow: 'none',
    backgroundColor: '#130f72 !important',
    color: '#ffc400',
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 700,
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#0f0c5c !important',
      boxShadow: '0 2px 8px rgba(19, 15, 114, 0.2)',
    },
    '&:active': {
      backgroundColor: '#0b0947 !important',
    },
  };

  const importIconButtonStyles = {
    width: 42,
    height: 42,
    borderRadius: '12px',
    backgroundColor: '#130f72',
    color: '#ffc400',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: '#0f0c5c',
      boxShadow: '0 2px 8px rgba(19, 15, 114, 0.2)',
    },
    '&:active': {
      backgroundColor: '#0b0947',
    },
    '&.Mui-disabled': {
      backgroundColor: '#130f72',
      color: '#ffc400',
      opacity: 0.65,
    },
  };

  const tooltipSlotProps = {
    tooltip: {
      sx: {
        bgcolor: '#130f72',
        color: '#ffc400',
        fontFamily: 'Poppins, sans-serif',
        fontSize: '0.82rem',
        fontWeight: 600,
        borderRadius: '10px',
        boxShadow: '0 6px 18px rgba(19, 15, 114, 0.25)',
        px: 1.25,
        py: 0.8,
      },
    },
    arrow: {
      sx: {
        color: '#130f72',
      },
    },
  };

  const singleLineAlertSx = {
    borderRadius: '12px',
    fontFamily: 'Poppins, sans-serif',
    fontWeight: 700,
    boxShadow: '0 10px 24px rgba(0,0,0,0.25)',
    color: '#fff',
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
    '& .MuiAlert-message': {
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      display: 'block',
      minWidth: 0,
    },
    '& .MuiAlert-icon': {
      color: '#fff',
      flexShrink: 0,
    },
    '& .MuiAlert-action': {
      alignItems: 'center',
    },
  };

  const filteredFaculties = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();

    const facultyOnly = faculties.filter(
      (faculty) => String(faculty.userRole || '').trim().toLowerCase() === 'faculty'
    );

    const filtered = !keyword
      ? facultyOnly
      : facultyOnly.filter((faculty) => {
          const firstName = (faculty.firstName || '').toLowerCase();
          const middleName = (faculty.middleName || '').toLowerCase();
          const lastName = (faculty.lastName || '').toLowerCase();
          const fullName = `${firstName} ${middleName} ${lastName}`.replace(/\s+/g, ' ').trim();
          const reverseFullName = `${lastName}, ${firstName} ${middleName}`.replace(/\s+/g, ' ').trim();

          return (
            firstName.includes(keyword) ||
            middleName.includes(keyword) ||
            lastName.includes(keyword) ||
            fullName.includes(keyword) ||
            reverseFullName.includes(keyword)
          );
        });

    return [...filtered].sort((a, b) => {
      const nameA = formatFullName(a).toLowerCase();
      const nameB = formatFullName(b).toLowerCase();
      return nameSort === 'az' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });
  }, [faculties, searchTerm, nameSort]);

  const filteredStudents = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();

    const filtered = !keyword
      ? students
      : students.filter((student) => {
          const firstName = (student.firstName || '').toLowerCase();
          const middleName = (student.middleName || '').toLowerCase();
          const lastName = (student.lastName || '').toLowerCase();
          const fullName = `${firstName} ${middleName} ${lastName}`.replace(/\s+/g, ' ').trim();
          const reverseFullName = `${lastName}, ${firstName} ${middleName}`.replace(/\s+/g, ' ').trim();

          return (
            firstName.includes(keyword) ||
            middleName.includes(keyword) ||
            lastName.includes(keyword) ||
            fullName.includes(keyword) ||
            reverseFullName.includes(keyword)
          );
        });

    return [...filtered].sort((a, b) => {
      const nameA = formatFullName(a).toLowerCase();
      const nameB = formatFullName(b).toLowerCase();
      return nameSort === 'az' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    });
  }, [students, searchTerm, nameSort]);

  const paginatedFaculties = filteredFaculties.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const paginatedStudents = filteredStudents.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const renderEmptyStateRow = (message, colSpan) => (
    <TableRow>
      <TableCell colSpan={colSpan} align="center" sx={{ py: 7, borderBottom: 'none' }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              backgroundColor: 'rgba(19, 15, 114, 0.08)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <InboxRoundedIcon
              sx={{
                fontSize: '2.2rem',
                color: '#130f72',
              }}
            />
          </Box>

          <Typography
            sx={{
              fontFamily: 'Space Grotesk, sans-serif',
              fontWeight: 700,
              color: '#130f72',
              fontSize: '1.05rem',
            }}
          >
            No records found
          </Typography>

          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              color: 'grey.400',
              fontSize: '0.92rem',
            }}
          >
            {message}
          </Typography>
        </Box>
      </TableCell>
    </TableRow>
  );

  const renderForm = (type) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          mb: 5,
          mx: 11,
          pl: 6,
          pr: 6.5,
          boxSizing: 'border-box',
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontFamily: 'Space Grotesk, sans-serif',
            color: '#130f72',
            fontWeight: 700,
          }}
        >
          {editMode
            ? `Edit ${type === 'student' ? 'Student' : 'Faculty'}`
            : `Add ${type === 'student' ? 'Student' : 'Faculty'}`}
        </Typography>

        {editMode && (
          <Button
            variant="contained"
            color="inherit"
            startIcon={<DeleteIcon sx={{ fontSize: '1rem', color: '#c53030' }} />}
            onClick={() => setDeleteOpen(true)}
            disabled={loading}
            sx={{
              py: 0.5,
              px: 2,
              borderRadius: '7px',
              boxShadow: 'none',
              fontFamily: 'Poppins, sans-serif',
              fontSize: '0.7rem',
              backgroundColor: '#fed7d7 !important',
              color: '#000000',
              transition: 'all 0.3s ease',
              '&:hover': {
                backgroundColor: '#f4c7c7 !important',
                boxShadow: '0 2px 8px rgba(244, 199, 199, 0.2)',
              },
              '&:active': {
                backgroundColor: '#e6b7b7 !important',
              },
            }}
            aria-label={`Remove ${type === 'student' ? 'Student' : 'Faculty'}`}
          >
            Remove {type === 'student' ? 'Student' : 'Faculty'}
          </Button>
        )}
      </Box>

      <form onSubmit={(e) => handleSubmit(e, type)} style={{ width: '100%' }}>
        <Grid container spacing={3} justifyContent="center">
          {type === 'student' ? (
            <>
              <Grid container item xs={12} spacing={3} sx={{ mb: 1 }}>
                <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TextField
                    select
                    label="Course"
                    InputLabelProps={{ required: false }}
                    name="course"
                    value={formData.course}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    aria-label="Course"
                    sx={inputFieldStyles}
                  >
                    {courses.map((course) => (
                      <MenuItem key={course} value={course}>
                        {course}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TextField
                    select
                    label="Year Level"
                    InputLabelProps={{ required: false }}
                    name="yearLevel"
                    value={formData.yearLevel}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    aria-label="Year Level"
                    sx={yearLevelInputFieldStyles}
                  >
                    {yearLevels.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TextField
                    select
                    label="Section"
                    InputLabelProps={{ required: false }}
                    name="section"
                    value={formData.section}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    aria-label="Section"
                    sx={reducedInputFieldStyles}
                  >
                    {sections.map((section) => (
                      <MenuItem key={section} value={section}>
                        {section}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              <Grid container item xs={12} spacing={3} sx={{ mb: 1 }}>
                <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TextField
                    label="Last Name"
                    placeholder="Last Name"
                    InputLabelProps={{ required: false }}
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    aria-label="Last Name"
                    sx={inputFieldStyles}
                  />
                </Grid>

                <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TextField
                    label="First Name"
                    placeholder="First Name"
                    InputLabelProps={{ required: false }}
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    aria-label="First Name"
                    sx={inputFieldStyles}
                  />
                </Grid>
              </Grid>

              <Grid container item xs={12} spacing={3} sx={{ mb: 1 }}>
                <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TextField
                    label="Middle Name"
                    placeholder="Middle Name"
                    InputLabelProps={{ required: false }}
                    name="middleName"
                    value={formData.middleName}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    aria-label="Middle Name"
                    sx={inputFieldStyles}
                  />
                </Grid>

                <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TextField
                    label="Contact Number"
                    placeholder="Contact Number"
                    InputLabelProps={{ required: false }}
                    name="contactNumber"
                    value={formData.contactNumber}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    aria-label="Contact Number"
                    sx={inputFieldStyles}
                  />
                </Grid>
              </Grid>

              <Grid container item xs={12} spacing={3} sx={{ pl: 3, mb: 1 }}>
                <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TextField
                    label="Student Number"
                    placeholder="Student Number"
                    InputLabelProps={{ required: false }}
                    name="studentNumber"
                    value={formData.studentNumber}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    aria-label="Student Number"
                    sx={inputFieldStyles}
                  />
                </Grid>

                <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TextField
                    label="RFID"
                    placeholder="RFID"
                    InputLabelProps={{ required: false }}
                    name="rfid"
                    value={formData.rfid}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    aria-label="RFID"
                    sx={inputFieldStyles}
                  />
                </Grid>
              </Grid>
            </>
          ) : (
            <>
              <Grid container item xs={12} spacing={3} sx={{ mb: 1 }}>
                <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TextField
                    label="Faculty Number"
                    placeholder="Faculty Number"
                    InputLabelProps={{ required: false }}
                    name="facultyId"
                    value={formData.facultyId}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    aria-label="Faculty Number"
                    sx={inputFieldStyles}
                  />
                </Grid>

                <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TextField
                    select
                    label="Department"
                    InputLabelProps={{ required: false }}
                    name="department"
                    value={formData.department}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    aria-label="Department"
                    sx={inputFieldStyles}
                  >
                    {departments.map((dep) => (
                      <MenuItem key={dep} value={dep}>
                        {dep}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              </Grid>

              <Grid container item xs={12} spacing={3} sx={{ mb: 1 }}>
                <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TextField
                    label="Last Name"
                    placeholder="Last Name"
                    InputLabelProps={{ required: false }}
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    aria-label="Last Name"
                    sx={inputFieldStyles}
                  />
                </Grid>

                <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TextField
                    label="First Name"
                    placeholder="First Name"
                    InputLabelProps={{ required: false }}
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    aria-label="First Name"
                    sx={inputFieldStyles}
                  />
                </Grid>
              </Grid>

              <Grid container item xs={12} spacing={3} sx={{ mb: 1 }}>
                <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TextField
                    label="Middle Name"
                    placeholder="Middle Name"
                    InputLabelProps={{ required: false }}
                    name="middleName"
                    value={formData.middleName}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    aria-label="Middle Name"
                    sx={inputFieldStyles}
                  />
                </Grid>

                <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TextField
                    label="Contact Number"
                    placeholder="Contact Number"
                    InputLabelProps={{ required: false }}
                    name="contactNumber"
                    value={formData.contactNumber}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                    aria-label="Contact Number"
                    sx={inputFieldStyles}
                  />
                </Grid>
              </Grid>

              <Grid container item xs={12} spacing={3} sx={{ pl: 3, mb: 1 }}>
                <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TextField
                    label="Email"
                    placeholder="Email"
                    InputLabelProps={{ required: false }}
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    aria-label="Email"
                    sx={inputFieldStyles}
                  />
                </Grid>

                <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: 'center' }}>
                  <TextField
                    label="RFID"
                    placeholder="RFID"
                    InputLabelProps={{ required: false }}
                    name="rfid"
                    value={formData.rfid}
                    onChange={handleChange}
                    fullWidth
                    required
                    variant="outlined"
                    aria-label="RFID"
                    sx={inputFieldStyles}
                  />
                </Grid>
              </Grid>
            </>
          )}

          <Grid item xs={12}>
            <Divider sx={{ my: 2, backgroundColor: 'rgba(79, 209, 197, 0.2)' }} />
          </Grid>
        </Grid>

        <Grid container justifyContent="flex-end" spacing={2} sx={{ mx: 6.5, mt: 3 }}>
          <Grid item xs={12} sm={3}>
            <Button
              variant="outlined"
              fullWidth
              onClick={() => {
                setModalOpen(false);
                resetForm();
              }}
              sx={{
                py: 1,
                borderRadius: '12px',
                boxShadow: 'none',
                transition: 'all 0.3s ease',
                borderColor: '#130f72',
                color: '#130f72',
                fontFamily: 'Poppins, sans-serif',
                '&:hover': {
                  boxShadow: 'none',
                  backgroundColor: 'rgba(19, 15, 114, 0.06)',
                  borderColor: '#130f72',
                },
              }}
              aria-label="Cancel"
            >
              Cancel
            </Button>
          </Grid>

          <Grid item xs={12} sm={3}>
            <Button
              type="submit"
              variant="contained"
              color="inherit"
              fullWidth
              disabled={loading}
              sx={{
                py: 1,
                borderRadius: '12px',
                boxShadow: 'none',
                backgroundColor: '#130f72 !important',
                color: '#ffc400',
                fontFamily: 'Poppins, sans-serif',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: '#0f0c5c !important',
                  boxShadow: '0 2px 8px rgba(19, 15, 114, 0.2)',
                },
                '&:active': {
                  backgroundColor: '#0b0947 !important',
                },
              }}
              aria-label={
                editMode
                  ? `Update ${type === 'student' ? 'Student' : 'Faculty'}`
                  : `Add ${type === 'student' ? 'Student' : 'Faculty'}`
              }
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: '#ffc400' }} />
              ) : editMode ? (
                `Update ${type === 'student' ? 'Student' : 'Faculty'}`
              ) : (
                `Add ${type === 'student' ? 'Student' : 'Faculty'}`
              )}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );

  if (initialLoading) {
    return (
      <Box
        sx={{
          minHeight: '70vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress sx={{ color: '#130f72', mb: 2 }} />
          <Typography sx={{ color: '#130f72', fontWeight: 700 }}>
            Loading user registry...
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        style={{ display: 'none' }}
        onChange={handleImportFile}
      />

      <Box
        sx={{
          position: 'fixed',
          top: 45,
          right: 55,
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          width: '300px',
          maxWidth: '300px',
        }}
      >
        {error && (
          <Alert
            severity="error"
            variant="filled"
            sx={{
              ...singleLineAlertSx,
              bgcolor: '#b90d0d',
            }}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert
            severity="success"
            variant="filled"
            sx={{
              ...singleLineAlertSx,
              bgcolor: '#0f56b3',
            }}
          >
            {success}
          </Alert>
        )}
      </Box>

      <Box>
        <Paper
          sx={{
            p: 3,
            m: 5,
            mt: '33px',
            pl: '40px',
            borderRadius: '15px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', md: 'center' },
              flexDirection: { xs: 'column', md: 'row' },
              gap: 2,
              mb: 2,
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="Students and Faculties tabs"
              sx={{
                fontFamily: 'Poppins, sans-serif',
                '& .MuiTab-root': {
                  color: 'grey.400',
                  fontFamily: 'Poppins, sans-serif',
                  '&.Mui-selected': {
                    color: '#130f72',
                  },
                  '&:hover': {
                    backgroundColor: 'rgba(19, 15, 114, 0.08)',
                    borderRadius: '15px',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#130f72',
                },
              }}
            >
              <Tab label="Faculties" />
              <Tab label="Students" />
            </Tabs>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                flexWrap: 'wrap',
                justifyContent: { xs: 'flex-start', md: 'flex-end' },
              }}
            >
              <TextField
                placeholder={tabValue === 0 ? 'Search faculty name' : 'Search student name'}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                sx={searchFieldStyles}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <SearchIcon
                        sx={{
                          color: 'grey.400',
                          '.Mui-focused &': {
                            color: '#130f72',
                          },
                        }}
                      />
                    </InputAdornment>
                  ),
                }}
              />

              <Tooltip
                title={tabValue === 0 ? 'Import Faculty' : 'Import Student'}
                arrow
                slotProps={tooltipSlotProps}
              >
                <span>
                  <IconButton
                    onClick={handleImportButtonClick}
                    disabled={importing}
                    aria-label={tabValue === 0 ? 'Import Faculty' : 'Import Student'}
                    sx={importIconButtonStyles}
                  >
                    {importing ? (
                      <CircularProgress size={18} sx={{ color: '#ffc400' }} />
                    ) : (
                      <FileUploadOutlinedIcon sx={{ fontSize: '1.65rem' }} />
                    )}
                  </IconButton>
                </span>
              </Tooltip>

              {tabValue === 0 && (
                <Button
                  variant="contained"
                  color="inherit"
                  onClick={() => openAddModal('faculty')}
                  sx={addButtonStyles}
                  aria-label="Add Faculty"
                  startIcon={<AddIcon sx={{ color: '#ffc400' }} />}
                >
                  Add Faculty
                </Button>
              )}

              {tabValue === 1 && (
                <Button
                  variant="contained"
                  color="inherit"
                  onClick={() => openAddModal('student')}
                  sx={addButtonStyles}
                  aria-label="Add Student"
                  startIcon={<AddIcon sx={{ color: '#ffc400' }} />}
                >
                  Add Student
                </Button>
              )}
            </Box>
          </Box>

          <TableContainer
            component={Paper}
            sx={{ borderRadius: '15px', boxShadow: '0 2px 4px rgba(0,0,0,0.03)' }}
          >
            <Table aria-label={tabValue === 0 ? 'Faculties table' : 'Students table'}>
              <TableHead>
                <TableRow>
                  {tabValue === 0 ? (
                    <>
                      <TableCell
                        sx={{
                          fontFamily: 'Poppins, sans-serif',
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                        onClick={handleNameSortToggle}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon sx={{ p: 1, fontSize: '2rem' }} />
                          Faculty Name
                          <Typography
                            component="span"
                            sx={{
                              fontFamily: 'Poppins, sans-serif',
                              fontSize: '0.8rem',
                              color: '#130f72',
                              fontWeight: 600,
                            }}
                          >
                            ({nameSort === 'az' ? 'A-Z' : 'Z-A'})
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>Department</TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                        Faculty Number
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                        RFID
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center" />
                    </>
                  ) : (
                    <>
                      <TableCell
                        sx={{
                          fontFamily: 'Poppins, sans-serif',
                          cursor: 'pointer',
                          userSelect: 'none',
                        }}
                        onClick={handleNameSortToggle}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <PersonIcon sx={{ p: 1, fontSize: '2rem' }} />
                          Student Name
                          <Typography
                            component="span"
                            sx={{
                              fontFamily: 'Poppins, sans-serif',
                              fontSize: '0.8rem',
                              color: '#130f72',
                              fontWeight: 600,
                            }}
                          >
                            ({nameSort === 'az' ? 'A-Z' : 'Z-A'})
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>Course</TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                        Year Level
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                        Section
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                        Student Number
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                        RFID
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center" />
                    </>
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {tabValue === 0 ? (
                  filteredFaculties.length === 0 ? (
                    renderEmptyStateRow(
                      searchTerm.trim()
                        ? 'No faculty matched your search.'
                        : 'There are no faculty records in the database yet.',
                      5
                    )
                  ) : (
                    paginatedFaculties.map((faculty) => (
                      <TableRow key={faculty.id}>
                        <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PersonIcon
                              sx={{
                                backgroundColor: '#130f72',
                                color: '#ffc400',
                                borderRadius: '50%',
                                mr: 2,
                                p: 1,
                                fontSize: '2rem',
                              }}
                            />
                            {formatFullName(faculty)}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>
                          {faculty.department}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                          {faculty.facultyId}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                          {faculty.rfid}
                        </TableCell>
                        <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                          <EditIcon
                            sx={{
                              color: '#130f72',
                              cursor: 'pointer',
                              transition: 'all 0.3s ease',
                              '&:hover': { color: '#0f0c5c' },
                              '&:active': { color: '#0b0947' },
                            }}
                            onClick={() => handleEdit(faculty.id, 'faculty')}
                            aria-label={`Edit faculty ${faculty.facultyId}`}
                          />
                        </TableCell>
                      </TableRow>
                    ))
                  )
                ) : filteredStudents.length === 0 ? (
                  renderEmptyStateRow(
                    searchTerm.trim()
                      ? 'No student matched your search.'
                      : 'There are no student records in the database yet.',
                    7
                  )
                ) : (
                  paginatedStudents.map((student) => (
                    <TableRow key={student.id}>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <PersonIcon
                            sx={{
                              backgroundColor: '#130f72',
                              color: '#ffc400',
                              borderRadius: '50%',
                              mr: 2,
                              p: 1,
                              fontSize: '2rem',
                            }}
                          />
                          {formatFullName(student)}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }}>
                        {student.course}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                        {student.yearLevel}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                        {student.section}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                        {student.studentNumber}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                        {student.rfid}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'Poppins, sans-serif' }} align="center">
                        <EditIcon
                          sx={{
                            color: '#130f72',
                            cursor: 'pointer',
                            transition: 'all 0.3s ease',
                            '&:hover': { color: '#0f0c5c' },
                            '&:active': { color: '#0b0947' },
                          }}
                          onClick={() => handleEdit(student.id, 'student')}
                          aria-label={`Edit student ${student.studentNumber}`}
                        />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            <TablePagination
              rowsPerPageOptions={[7]}
              component="div"
              count={tabValue === 0 ? filteredFaculties.length : filteredStudents.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              sx={{
                fontFamily: 'Poppins, sans-serif',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                  fontFamily: 'Poppins, sans-serif',
                  color: 'grey.400',
                },
                '& .MuiTablePagination-actions': {
                  '& .MuiIconButton-root': {
                    color: '#130f72',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      color: '#0f0c5c',
                    },
                    '&.Mui-disabled': {
                      color: 'grey.400',
                    },
                  },
                },
                '& .MuiSvgIcon-root': {
                  color: 'inherit',
                },
              }}
            />
          </TableContainer>
        </Paper>

        <Modal
          open={modalOpen}
          onClose={() => {
            setModalOpen(false);
            resetForm();
          }}
          closeAfterTransition
          disableEscapeKeyDown
          aria-labelledby="form-modal-title"
          aria-describedby="form-modal-description"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              width: { xs: '90%', md: '900px' },
              height: { xs: 'auto', md: '550px' },
              bgcolor: '#f4f7fe',
              borderRadius: '25px',
              boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                width: { xs: '100%', md: '25%' },
                display: { xs: 'none', md: 'block' },
                backgroundImage: `url(${SignInImg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                borderBottomLeftRadius: '25px',
              }}
            />
            <Box
              sx={{
                width: { xs: '100%', md: '75%' },
                p: { xs: 1.5, md: 3 },
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                bgcolor: '#f8fafc',
              }}
            >
              {formType && renderForm(formType)}
            </Box>
          </Box>
        </Modal>

        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
          <DialogTitle
            sx={{
              fontFamily: '"Space Grotesk", sans-serif',
              fontWeight: 700,
              color: '#130f72',
            }}
          >
            Remove Item
          </DialogTitle>

          <DialogContent>
            <DialogContentText
              sx={{
                fontFamily: 'Poppins, sans-serif',
                marginX: 5,
              }}
            >
              Are you sure you want to remove this {formType === 'student' ? 'student' : 'faculty'}?
            </DialogContentText>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => setDeleteOpen(false)}
              sx={{
                fontFamily: 'Poppins, sans-serif',
                color: '#130f72',
              }}
            >
              Cancel
            </Button>

            <Button
              onClick={handleDelete}
              autoFocus
              color="error"
              disabled={loading}
              sx={{
                fontFamily: 'Poppins, sans-serif',
              }}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : 'Remove'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default UserRegistry;